import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import {
  InclusionAgentDocSuggestion,
  InclusionAgentStatKey,
  inclusionAgentStatTranslationMapping,
} from "@/models/InclusionAgentModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type InclusionAgentStatsProps = {
  suggestion: InclusionAgentDocSuggestion;
  className?: string;
};

export const hasStats = (suggestion: InclusionAgentDocSuggestion) => {
  return !!(suggestion.tbr || suggestion.tir || suggestion.cv || suggestion.gmi || suggestion.hypo_count);
};

export const InclusionAgentStats: React.FC<InclusionAgentStatsProps> = ({ suggestion, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const statsEntries: { key: InclusionAgentStatKey; value: number | undefined }[] = [
    { key: "tbr", value: suggestion.tbr },
    { key: "tir", value: suggestion.tir },
    { key: "cv", value: suggestion.cv },
    { key: "gmi", value: suggestion.gmi },
    { key: "hypo_count", value: suggestion.hypo_count },
  ];

  return (
    <>
      {statsEntries
        .filter(({ value }) => value !== undefined)
        .map(({ key, value }) => (
          <div key={`stats-${key}-${suggestion.id}`} className={cx(styles.container, className)}>
            <Typography variant="subtitle">{t(inclusionAgentStatTranslationMapping(key))}</Typography>
            <Tag color="pink" label={key === "hypo_count" ? `${value}` : `${value}%`} />
          </div>
        ))}
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: theme.spacing(48),
    justifyContent: "space-between",
    gap: theme.spacing(8),
  }),
});
